'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { getSendrealmClient } from '@sendrealm/react';

import api from '../services/api';
import { initializeSendrealmWebPush } from '../services/SendrealmPush';
import AuthContext from './AuthContext';

interface SendrealmPushContextValue {
  initialized: boolean;
  loading: boolean;
  subscribed: boolean;
  permissionStatus: string;
  error: unknown | null;
  enable(): Promise<void>;
  disable(): Promise<void>;
}

const SendrealmPushContext = createContext<SendrealmPushContextValue>(
  {} as SendrealmPushContextValue
);

export function useSendrealmPush() {
  return useContext(SendrealmPushContext);
}

export function SendrealmPushProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const auth = useContext(AuthContext);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [error, setError] = useState<unknown | null>(null);

  const refreshState = useCallback(async () => {
    const client = getSendrealmClient();
    const [state, permission] = await Promise.all([
      client.getState(),
      client.getPermissionStatus()
    ]);
    setSubscribed(state.subscribed);
    setPermissionStatus(permission);
  }, []);

  const syncServer = useCallback(async (enabled: boolean) => {
    await api.put('/v1/me/push-notifications/web', { enabled });
  }, []);

  useEffect(() => {
    let mounted = true;
    let permissionObserver: { remove(): void } | undefined;
    let subscriptionObserver: { remove(): void } | undefined;
    let notificationClickListener: { remove(): void } | undefined;

    if (auth.isLoading) return;

    if (!auth.isLoggedIn || !auth.userData?._id || !auth.userData.email) {
      setInitialized(false);
      setLoading(false);
      setSubscribed(false);
      setPermissionStatus('unknown');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    initializeSendrealmWebPush()
      .then(async () => {
        if (!mounted) return;

        const client = getSendrealmClient();
        permissionObserver = client.addPermissionObserver(event => {
          if (mounted) setPermissionStatus(event.status);
        });
        subscriptionObserver = client.addSubscriptionObserver(event => {
          if (mounted) setSubscribed(event.subscribed);
        });
        notificationClickListener = client.addNotificationClickListener(
          event => {
            if (!event.launchUrl) return;
            try {
              const launchUrl = new URL(
                event.launchUrl,
                window.location.origin
              );
              if (launchUrl.origin === window.location.origin) {
                window.location.assign(launchUrl.href);
              }
            } catch {}
          }
        );

        await refreshState();
        if (mounted) {
          setInitialized(true);
          setLoading(false);
        }
      })
      .catch(initializationError => {
        if (mounted) {
          setError(initializationError);
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
      permissionObserver?.remove();
      subscriptionObserver?.remove();
      notificationClickListener?.remove();
    };
  }, [
    auth.isLoading,
    auth.isLoggedIn,
    auth.userData?._id,
    auth.userData?.email,
    refreshState
  ]);

  useEffect(() => {
    if (!initialized || auth.isLoading) return;

    const client = getSendrealmClient();
    if (auth.isLoggedIn && auth.userData?._id && auth.userData.email) {
      client
        .login(auth.userData._id, auth.userData.email)
        .then(async () => {
          const isSubscribed = await client.isSubscribed();
          await syncServer(isSubscribed);
          await refreshState();
        })
        .catch(linkError => setError(linkError));
      return;
    }

    client.logout().catch(unlinkError => setError(unlinkError));
  }, [
    auth.isLoading,
    auth.isLoggedIn,
    auth.userData?._id,
    auth.userData?.email,
    initialized,
    refreshState,
    syncServer
  ]);

  const enable = useCallback(async () => {
    if (!auth.isLoggedIn || !auth.userData?._id || !auth.userData.email) {
      throw new Error('A signed-in user is required for web push');
    }
    if (!initialized) {
      throw new Error('Sendrealm web push is not initialized');
    }

    setLoading(true);
    setError(null);
    try {
      const client = getSendrealmClient();
      const enabled = await client.optIn();
      if (!enabled) throw new Error('Browser notification permission denied');
      await client.login(auth.userData._id, auth.userData.email);
      await syncServer(true);
      await refreshState();
    } catch (enableError) {
      setError(enableError);
      throw enableError;
    } finally {
      setLoading(false);
    }
  }, [
    auth.isLoggedIn,
    auth.userData?._id,
    auth.userData?.email,
    initialized,
    refreshState,
    syncServer
  ]);

  const disable = useCallback(async () => {
    if (!auth.isLoggedIn) {
      throw new Error('A signed-in user is required for web push');
    }

    setLoading(true);
    setError(null);
    try {
      await initializeSendrealmWebPush();
      const disabled = await getSendrealmClient().optOut();
      if (!disabled) throw new Error('Unable to disable browser notifications');
      await syncServer(false);
      await refreshState();
    } catch (disableError) {
      setError(disableError);
      throw disableError;
    } finally {
      setLoading(false);
    }
  }, [auth.isLoggedIn, refreshState, syncServer]);

  const value = useMemo(
    () => ({
      initialized,
      loading,
      subscribed,
      permissionStatus,
      error,
      enable,
      disable
    }),
    [disable, enable, error, initialized, loading, permissionStatus, subscribed]
  );

  return (
    <SendrealmPushContext.Provider value={value}>
      {children}
    </SendrealmPushContext.Provider>
  );
}
